import type { FileEntry, Filter, Folder } from "./types";

/** Voller Pfad eines Ordners — die Beschriftung in jeder Auswahlliste. */
export function pfadVon(f: Folder, byId: Map<string, Folder>): string {
  const teile = [f.name];
  let cur = f.parent_id ?? null;
  // Begrenzt, damit eine kaputte Elternkette die Seite nicht aufhängt.
  for (let i = 0; cur && i < 64; i++) {
    const p = byId.get(cur);
    if (!p) break;
    teile.unshift(p.name);
    cur = p.parent_id ?? null;
  }
  return teile.join(" / ");
}

/** Alle Ordner unterhalb von `root`, `root` eingeschlossen. Verhindert, dass
 *  ein Ordner in sein eigenes Kind verschoben wird — die Oberfläche bietet ein
 *  solches Ziel gar nicht erst an, statt den Server ablehnen zu lassen. */
export function teilbaum(root: string, folders: Folder[]): Set<string> {
  const kinder = new Map<string, string[]>();
  for (const f of folders) {
    const p = f.parent_id ?? "";
    (kinder.get(p) ?? kinder.set(p, []).get(p)!).push(f.id);
  }
  const out = new Set<string>([root]);
  const stapel = [root];
  while (stapel.length) {
    for (const k of kinder.get(stapel.pop()!) ?? []) {
      if (out.has(k)) continue;
      out.add(k);
      stapel.push(k);
    }
  }
  return out;
}

/** Ob ein Eintrag zum gewählten Reiter gehört.
 *
 *  "container" fragt nach der Sichtbarkeit, die anderen nach dem Bereich.
 *  Öffentliche Dateien bleiben zusätzlich in ihrem Bereich sichtbar — sie
 *  sind dort nicht verschwunden, sondern offen. */
export function gehoertZu(
  e: { module_id?: string },
  filter: Filter,
): boolean {
  if (filter === "all") return true;
  return e.module_id === filter;
}

/** Die Kette vom Wurzelordner bis zum aktuellen, für die Brotkrumen. */
export function ahnen(
  id: string | null,
  byId: Map<string, Folder>,
): Folder[] {
  const out: Folder[] = [];
  let cur = id;
  for (let i = 0; cur && i < 64; i++) {
    const f = byId.get(cur);
    if (!f) break;
    out.unshift(f);
    cur = f.parent_id ?? null;
  }
  return out;
}

/** Wie viele Einträge direkt in einem Ordner liegen. */
export function inhaltAnzahl(
  folderId: string,
  folders: Folder[],
  files: FileEntry[],
): number {
  let n = 0;
  for (const f of folders) if ((f.parent_id ?? null) === folderId) n++;
  for (const f of files) if ((f.folder_id ?? null) === folderId) n++;
  return n;
}

/** Größe aller Dateien im Teilbaum. */
export function teilbaumGroesse(
  root: string,
  folders: Folder[],
  files: FileEntry[],
): number {
  const drin = teilbaum(root, folders);
  let n = 0;
  for (const f of files) if (f.folder_id && drin.has(f.folder_id)) n += f.size;
  return n;
}

/** Menschenlesbare Dateigröße. */
export function formatSize(bytes: number): string {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

/** Dateiart aus MIME-Typ und Endung — bestimmt Symbol und Farbe.
 *  Dieselbe Zuordnung wie in home-storage. */
export type Art =
  | "image" | "pdf" | "code" | "text" | "video" | "audio" | "archive" | "html" | "other";

const CODE_EXT = new Set(["js", "ts", "jsx", "tsx", "go", "py", "rs", "java", "c", "h",
  "cpp", "sh", "fish", "sql", "yml", "yaml", "toml", "json", "xml", "css", "scss",
  "php", "rb", "lua", "vim", "conf", "ini", "env", "dockerfile"]);
const TEXT_EXT = new Set(["txt", "md", "log", "csv", "cfg", "license", "readme"]);
const ARCH_EXT = new Set(["zip", "tar", "gz", "tgz", "bz2", "xz", "7z", "rar"]);

export function artVon(mime: string | undefined, name: string): Art {
  const ext = (name.split(".").pop() ?? "").toLowerCase();
  const m = mime ?? "";
  if (m.startsWith("image/")) return "image";
  if (m === "application/pdf" || ext === "pdf") return "pdf";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("text/html") || ext === "html") return "html";
  if (CODE_EXT.has(ext)) return "code";
  if (m.startsWith("text/") || TEXT_EXT.has(ext) || m === "application/json") return "text";
  if (ARCH_EXT.has(ext)) return "archive";
  return "other";
}

/** Die Farbklasse aus `ui.css` zu einer Dateiart. */
export function farbklasse(art: Art): string {
  switch (art) {
    case "image": return "c-image";
    case "pdf": return "c-pdf";
    case "code": case "html": return "c-code";
    case "text": return "c-text";
    case "video": case "audio": return "c-media";
    case "archive": return "c-archive";
    default: return "c-file";
  }
}

/** Der Name, der dastehen soll.
 *
 *  Zwei Dateien gleichen Namens im selben Ordner sind in der Anwendung
 *  erlaubt, auf der Platte nicht — die zweite heißt dort „brief_2.pdf".
 *  Stand überall nur `name`, sah man zwei Zeilen „brief.pdf" untereinander
 *  und hielt sie für dieselbe Datei. Der echte Name sagt, dass es zwei sind,
 *  und man findet sie im Ordner wieder.
 */
export function anzeigeName(f: { name: string; disk_name?: string }): string {
  return f.disk_name || f.name;
}

/** Namen so vergleichen, wie ein Mensch zählt: 1, 2, 3 … 10, 11 — nicht
 *  1, 10, 11, 2.
 *
 *  `localeCompare` ohne `numeric` liest Ziffern als Buchstaben, und dann
 *  steht „10" vor „2", weil „1" vor „2" kommt. Ein Sammler statt jedes Mal
 *  neuer Optionen: der Vergleich läuft in jeder Sortierung tausendfach.
 *
 *  Überall in der Ablage wird hierüber sortiert — Baum, Liste, Zielwahl,
 *  Moodle-Ansicht. Vorher sortierte jede Stelle für sich, und alle gleich
 *  falsch. */
const namensFolge = new Intl.Collator(undefined, { numeric: true });

export function vergleicheNamen(a: string, b: string): number {
  return namensFolge.compare(a, b);
}
